import React, { useState, useRef, useEffect } from "react";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import { GlobalIcon, LogoIcon, Menu, Close } from "@assets/icons";
import P from "@components/common/P/P";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";
import { motion, AnimatePresence } from "framer-motion";

export const languages = [
	{ value: "tr", label: "Türkçe" },
	{ value: "en", label: "English" },
	{ value: "ru", label: "Русский" },
];

const menuVariants = {
	hidden: { opacity: 0, y: -20 },
	visible: { opacity: 1, y: 0 },
	exit: { opacity: 0, y: -20 },
};

const dropdownVariants = {
	hidden: { opacity: 0, scale: 0.95, y: -5 },
	visible: { opacity: 1, scale: 1, y: 0 },
	exit: { opacity: 0, scale: 0.95, y: -5 },
};

const MobileNavbar: React.FC = () => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isLangOpen, setIsLangOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);
	const { i18n, t } = useTranslation();
	const navigate = useNavigate();

	const changeLanguage = (lang: string) => {
		i18n.changeLanguage(lang);
		localStorage.setItem("lang", lang);
		setIsLangOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setIsMenuOpen(false);
				setIsLangOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div className="flex items-center justify-between px-6 py-4 bg-white border-b border-strokeColor relative">
			{/* Logo */}
			<div onClick={() => navigate(ROUTES.HOME)} className="cursor-pointer">
				<SvgIcon Icon={LogoIcon} />
			</div>

			{/* Hamburger Icon */}
			<div
				onClick={() => setIsMenuOpen(!isMenuOpen)}
				className="cursor-pointer"
			>
				<SvgIcon Icon={isMenuOpen ? Close : Menu} />
			</div>

			{/* Slide-out Menu */}
			<AnimatePresence>
				{isMenuOpen && (
					<motion.div
						ref={menuRef}
						initial="hidden"
						animate="visible"
						exit="exit"
						variants={menuVariants}
						transition={{ duration: 0.25 }}
						className="absolute top-16 left-0 w-full bg-white shadow-md z-50 flex flex-col gap-6 p-6"
					>
						<P
							size="p3"
							type="regular"
							className="cursor-pointer"
							onClick={() => {
								navigate(ROUTES.PRODUCTS);
								setIsMenuOpen(false);
							}}
						>
							{t("navbar.products")}
						</P>
						<P
							size="p3"
							type="regular"
							className="cursor-pointer"
							onClick={() => {
								navigate(ROUTES.ABOUT);
								setIsMenuOpen(false);
							}}
						>
							{t("navbar.about")}
						</P>
						<P
							size="p3"
							type="regular"
							className="cursor-pointer"
							onClick={() => {
								navigate(ROUTES.FAQ);
								setIsMenuOpen(false);
							}}
						>
							{t("navbar.faq")}
						</P>
						<P
							size="p3"
							type="regular"
							className="cursor-pointer"
							onClick={() => {
								navigate(ROUTES.CONTACT);
								setIsMenuOpen(false);
							}}
						>
							{t("navbar.contact")}
						</P>

						{/* Language Switcher */}
						<div className="relative">
							<div
								className="flex items-center gap-2 cursor-pointer"
								onClick={() => setIsLangOpen(!isLangOpen)}
							>
								<SvgIcon Icon={GlobalIcon} />
								<P size="p3" type="regular">
									{languages.find((l) => l.value === i18n.language)?.label}
								</P>
							</div>

							<AnimatePresence>
								{isLangOpen && (
									<motion.div
										initial="hidden"
										animate="visible"
										exit="exit"
										variants={dropdownVariants}
										transition={{ duration: 0.2 }}
										className="mt-2 bg-white rounded shadow-md border absolute left-0 z-10"
									>
										{languages.map((lang) => (
											<div
												key={lang.value}
												className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
												onClick={() => changeLanguage(lang.value)}
											>
												{lang.label}
											</div>
										))}
									</motion.div>
								)}
							</AnimatePresence>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default MobileNavbar;
